import React, { useMemo } from "react";
import { ChatListItem } from "@web-src/modules/chats/components/ChatListItem";
import { ChatListItemLastMessage } from "@web-src/modules/chats/components/ChatListItemLastMessage";
import { tokenizeMessageBody } from "@web-src/modules/chats/utils";
import { CHAT_MESSAGE_PAYLOAD_VERSION } from "@jugl-web/utils/consts";
import { InstagramConversationModel } from "@jugl-web/rest-api/integrations/models/InstagramConversation";
import { instagramMessageToAttachmentsPayload } from "@web-src/modules/client-support/utils/instagramMessageToAttachmentsPayload";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useTranslations } from "@jugl-web/utils";
import InstagramIcon from "./assets/instagram.png";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { isInstagramMessageSupported } from "../../../../utils/isInstagramMessageSupported";
import { CRMAgentInfo } from "../CRMAgentInfo";
import { isInstagramMessageDeleted } from "../../../../utils/isInstagramMessageDeleted";

export const InstagramConversationTile: React.FC<{
  conversation: InstagramConversationModel;
  isDraftUnavailable: boolean;
  searchQuery?: string;
}> = ({ conversation, isDraftUnavailable, searchQuery }) => {
  const { selectedConversation, selectConversation } =
    useClientSupportPageContext();
  const { entityId } = useEntitySelectedProvider();
  const { t } = useTranslations();

  const attachments = useMemo(
    () =>
      instagramMessageToAttachmentsPayload({
        message: conversation,
        entityId,
        t,
      }) || undefined,
    [conversation, entityId, t]
  );

  const isUnsupported = useMemo(
    () => !isInstagramMessageSupported(conversation),
    [conversation]
  );

  const isDeleted = useMemo(
    () => isInstagramMessageDeleted(conversation),
    [conversation]
  );

  const lastMessageBody = useMemo(() => {
    if (
      "message" in conversation.payload &&
      "text" in conversation.payload.message
    ) {
      return tokenizeMessageBody(conversation.payload.message.text);
    }

    return "";
  }, [conversation]);

  return (
    <ChatListItem
      chatId={conversation.conv_id}
      onClick={() => selectConversation(conversation)}
      isSelected={selectedConversation?.conv_id === conversation.conv_id}
      title={conversation.name}
      img={InstagramIcon}
      timestamp={conversation.inserted_at}
      lastMessageContent={
        <ChatListItemLastMessage
          payloadVersion={CHAT_MESSAGE_PAYLOAD_VERSION}
          tokenizedMessage={lastMessageBody || []}
          attachments={attachments}
          isUnsupported={isUnsupported}
          isDeleted={isDeleted}
        />
      }
      isDraftUnavailable={isDraftUnavailable}
      additionalContent={<CRMAgentInfo conversation={conversation} />}
      showUnreadIndicator={conversation.type === "incoming"}
      isUnsupported={isUnsupported}
      hasAttachments={!!attachments?.length}
      searchQuery={searchQuery}
      hasChatAccess
    />
  );
};
